import type { ApiTokenView } from '../../services/api-tokens';
import { formatDateTime } from '../../utils/formatters';

/**
 * How a token's lifecycle is shown (#41 part 3). A disabled token is visibly different from an active one,
 * says WHY in plain language, and offers no Revoke (there is nothing left to revoke).
 */
export type TokenState = 'active' | 'revoked' | 'inactive' | 'expired';

export interface TokenStatus {
  state: TokenState;
  /** The badge text. */
  badge: string;
  /** Plain-language detail shown beside the badge; null for an active token. */
  reason: string | null;
  /** Revoke is only meaningful while the token still works. */
  canRevoke: boolean;
}

export function describeTokenStatus(
  t: Pick<ApiTokenView, 'disabledAt' | 'disabledReason'> & { expiresAt?: string | null },
  now: number = Date.now()
): TokenStatus {
  // Authentication already refuses a token past its expiry even before anything has marked it disabled.
  if (!t.disabledAt && t.expiresAt && Date.parse(t.expiresAt) <= now) {
    return { state: 'expired', badge: 'Expired', reason: `Expired on ${formatDateTime(t.expiresAt)}.`, canRevoke: false };
  }
  if (!t.disabledAt) return { state: 'active', badge: 'Active', reason: null, canRevoke: true };
  const on = formatDateTime(t.disabledAt);
  switch (t.disabledReason) {
    case 'manual':
      return { state: 'revoked', badge: 'Revoked', reason: `Revoked on ${on}. Scripts using it no longer work.`, canRevoke: false };
    case 'inactivity':
      return { state: 'inactive', badge: 'Disabled', reason: `Disabled on ${on}: it went unused for too long.`, canRevoke: false };
    case 'expired':
      // The expiry moment, not when the sweep got around to disabling it.
      return { state: 'expired', badge: 'Expired', reason: `Expired on ${formatDateTime(t.expiresAt ?? t.disabledAt)}.`, canRevoke: false };
    default:
      // disabledAt without a known reason (the schema forbids it): still disabled, never offered a Revoke.
      return { state: 'revoked', badge: 'Disabled', reason: `Disabled on ${on}.`, canRevoke: false };
  }
}

/** The badge class per state (existing status colours; works in light and dark). */
export const TOKEN_BADGE_CLASS: Record<TokenState, string> = {
  active: 'status-active',
  revoked: 'status-down',
  inactive: 'status-pending',
  expired: 'status-disabled',
};
